import React, { useMemo } from 'react';
import { useTable } from 'react-table';
import { parse, format, subDays } from 'date-fns';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import DatePicker from '../../../../shared/_TBComponents/DatePicker';
import Icon from '../../../../shared/_TBComponents/Icon';
import SelectBox from '../../../../shared/_TBComponents/SelectBox';
import Text from '../../../../shared/_TBComponents/Text';
import Toggle from '../../../../shared/_TBComponents/Toggle';
import IconActionButton from './IconActionButton';

const FormWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

const InfoWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 12px;
  background: #F7F7F7;
  border: 1px solid #9e9e9e;
  border-radius: 4px;
`;

const CalendarOptionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding-left: ${(props) => `${props.level * 16}px`};
`;

const ToggleContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-top: 8px;
`;

const DateRangeRow = styled.div`
  display: flex;
  gap: 16px;
  align-items: flex-start;

  @media (max-width: 767px) {
    flex-direction: column;
  }
`;

const DatePickerField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const DateOffsetContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 8px;
`;

const TableWrapper = styled.div`
  width: 100%;
  overflow-x: auto;

  table {
    width: 100%;
    border-collapse: collapse;
  }

  th {
    padding: 8px 12px;
    font-weight: 600;
    border-bottom: 1px solid #EEEEEE;
    background: #FAFAFA;
  }

  th:first-child,
  td:first-child {
    text-align: left;
  }

  th:not(:first-child),
  td:not(:first-child) {
    text-align: center;
  }

  td {
    padding: 10px 12px;
    border-bottom: 1px solid #F5F5F5;
    vertical-align: middle;
  }

  tbody tr:last-of-type td {
    border-bottom: none;
  }
`;

const ActiveBadge = styled.div`
  display: inline-block;
  background: #EEEEEE;
  color: #333333;
  font-size: 12px;
  padding: 4px 8px;
  border-radius: 4px;
  margin-left: 8px;
`;

// Cell renderers
const NameCell = ({ row }) => (
  <span>
    {row.original.calendarName}
    {row.original.isActiveCalendar && <ActiveBadge>Active</ActiveBadge>}
  </span>
);

NameCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      calendarName: PropTypes.string,
      isActiveCalendar: PropTypes.bool,
    }).isRequired,
  }).isRequired,
};

const DateCell = ({ value }) => (value && value !== 'N/A' ? value : '');

const ManageCell = ({ row }) => {
  const onOpen = row.original.onOpenNestedModalProp;
  const rowIndex = row.original.rowIndexProp;

  return (
    <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
      <IconActionButton
        type='edit'
        onClick={(e) => { e.preventDefault(); if (onOpen) onOpen('editCalendar', rowIndex); }}
      />
      <IconActionButton
        type='delete'
        onClick={(e) => { e.preventDefault(); if (onOpen) onOpen('deleteCalendar', rowIndex); }}
      />
    </div>
  );
};

DateCell.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

ManageCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      onOpenNestedModalProp: PropTypes.func,
      rowIndexProp: PropTypes.number,
    }).isRequired,
  }).isRequired,
};

const AthleteCalendarForm = ({
  athleteCalendars,
  calendars,
  calendarVisibilityOptions,
  values,
  selectBoxStyles,
  setFieldValue,
  onOpenNestedModal,
  isNew,
}) => {
  const calendarMap = useMemo(
    () => calendars.reduce((acc, calendar) => {
      acc[calendar.id] = calendar;
      return acc;
    }, {}),
    [calendars],
  );

  const getCalendarLevel = (calendar) => {
    let level = 0;
    let currentParentId = calendar.parentCalendarId;

    while (currentParentId && calendarMap[currentParentId]) {
      level += 1;
      currentParentId = calendarMap[currentParentId].parentCalendarId;
    }

    return level;
  };

  const formatCalendarOptionLabel = (option, { context }) => {
    const level = getCalendarLevel(option);
    const isChild = level > 0;

    if (context === 'value') {
      return option.name;
    }

    return (
      <CalendarOptionRow level={level}>
        {isChild ? (
          <Icon
            icon='sub-arrow'
            size={12}
            color='#9E9E9E'
            styledProps={{ marginBottom: '6px' }}
          />
        ) : null}
        <Text styledTextProps={{ color: '#333333', fontWeight: 600 }}>{option.name}</Text>
      </CalendarOptionRow>
    );
  };

  // Table data and columns
  const data = useMemo(() => (athleteCalendars || [])
    .map((calendar, idx) => ({
      ...calendar,
      displayStart: calendar.dateStart && calendar.dateStart !== 'N/A' ? calendar.dateStart : '-',
      displayEnd: calendar.dateEnd && calendar.dateEnd !== 'N/A' ? calendar.dateEnd : '-',
      displayOffset: calendar.dateOffset && calendar.dateOffset !== 'N/A' ? calendar.dateOffset : '-',
      onOpenNestedModalProp: onOpenNestedModal,
      rowIndexProp: idx,
    }))
    .sort((a, b) => {
      if (a.isActiveCalendar) return -1;
      if (b.isActiveCalendar) return 1;
      const dateA = a.dateStart && a.dateStart !== 'N/A' ? a.dateStart : '';
      const dateB = b.dateStart && b.dateStart !== 'N/A' ? b.dateStart : '';
      return dateB.localeCompare(dateA);
    }), [athleteCalendars, onOpenNestedModal]);

  const columns = useMemo(() => [
    { Header: 'Calendar Name', accessor: 'calendarName', Cell: NameCell },
    { Header: 'Start Date', accessor: 'displayStart', Cell: DateCell },
    { Header: 'End Date', accessor: 'displayEnd', Cell: DateCell },
    { Header: 'Offset', accessor: 'displayOffset', Cell: DateCell },
    {
      Header: 'Manage', accessor: 'manage', disableSortBy: true, Cell: ManageCell,
    },
  ], []);

  const {
    getTableProps, getTableBodyProps, headerGroups, rows, prepareRow,
  } = useTable({ columns, data });

  return (
    <FormWrapper>
      <InfoWrapper>
        <Text styledTextProps={{ fontSize: '16px', fontWeight: 600 }}>
          Calendars are used to assign workouts to athletes.
          An athlete can only have one calendar assigned at a time.
        </Text>
        {!values.calendarAssignment?.id && (
          <Text styledTextProps={{ fontSize: '16px', fontWeight: 600 }}>
            Select a calendar from the dropdown below to assign to this athlete.
          </Text>
        )}
      </InfoWrapper>

      <SelectBox
        title={isNew ? 'Add Calendar Assignment' : 'Change Calendar Assignment'}
        options={calendars}
        value={values.calendarAssignment}
        formatOptionLabel={formatCalendarOptionLabel}
        onChangeFunction={(opt) => {
          setFieldValue('calendarAssignment', opt);
          if (opt?.id) {
            setFieldValue('startDate', format(new Date(), 'MM/dd/yyyy'));
          }
        }}
        placeholder='Select a Calendar'
        styledProps={{ border: '1px solid #EEEEEE' }}
        titleStyledProps={{ fontWeight: 700 }}
        customStyles={selectBoxStyles}
        defaultValue={{}}
        height='auto'
      />

      {values.calendarAssignment?.id && (
        <>
          <Text styledTextProps={{ color: '#9E9E9E' }}>
            Select a date range for this calendar assignment.
          </Text>
          <DateRangeRow>
            <DatePickerField>
              <Text styledTextProps={{ fontSize: '14px', fontWeight: 700 }}>Start Date</Text>
              <DatePicker
                singleDate
                startDate={values.startDate ? parse(values.startDate, 'MM/dd/yyyy', new Date()) : null}
                setStartDate={(date) => {
                  setFieldValue('startDate', date ? format(date, 'MM/dd/yyyy') : '');
                  if (date && values.endDate) {
                    const end = parse(values.endDate, 'MM/dd/yyyy', new Date());
                    if (end < date) setFieldValue('endDate', '');
                  }
                  if (date && values.dateOffset) {
                    const offset = parse(values.dateOffset, 'MM/dd/yyyy', new Date());
                    if (offset >= date) setFieldValue('dateOffset', '');
                  }
                }}
                maxDate={null}
                placeholderText='Select start date'
                textColor='#333333'
              />
            </DatePickerField>
            <DatePickerField>
              <Text styledTextProps={{ fontSize: '14px', fontWeight: 700 }}>
                End Date
                {' '}
                <span style={{ fontSize: '12px', color: '#9E9E9E', fontWeight: 400 }}>(Leave blank if no end date)</span>
              </Text>
              <DatePicker
                singleDate
                startDate={values.endDate ? parse(values.endDate, 'MM/dd/yyyy', new Date()) : null}
                setStartDate={(date) => setFieldValue('endDate', date ? format(date, 'MM/dd/yyyy') : '')}
                minDate={values.startDate ? parse(values.startDate, 'MM/dd/yyyy', new Date()) : undefined}
                maxDate={null}
                placeholderText='Select end date'
                textColor='#333333'
              />
            </DatePickerField>
          </DateRangeRow>
          <DateOffsetContainer>
            <ToggleContainer>
              <Toggle
                defaultChecked={Boolean(values.dateOffset)}
                onChange={(e) => {
                  if (e.target.checked) {
                    setFieldValue('dateOffset', '');
                  } else {
                    setFieldValue('dateOffset', null);
                  }
                }}
                onHoverColor='#0DCC8A'
                onColor='#0DCC8A'
                offColor='#E0E0E0'
                thumbColorOff
                thumbBorderOff
                width='auto'
              />
              <Text styledTextProps={{ color: '#333333' }}>
                Enable Calendar Offset (Set Specific Date as First Workout)
              </Text>
            </ToggleContainer>
            {values.dateOffset !== null && (
              <DatePickerField>
                <Text styledTextProps={{ fontSize: '14px', fontWeight: 700 }}>
                  Offset Date
                  {' '}
                  <span style={{ fontSize: '12px', color: '#9E9E9E', fontWeight: 400 }}>(Optional)</span>
                </Text>
                <DatePicker
                  singleDate
                  startDate={values.dateOffset ? parse(values.dateOffset, 'MM/dd/yyyy', new Date()) : null}
                  setStartDate={(date) => setFieldValue('dateOffset', date ? format(date, 'MM/dd/yyyy') : '')}
                  maxDate={values.startDate ? subDays(parse(values.startDate, 'MM/dd/yyyy', new Date()), 1) : null}
                  placeholderText='Select offset date'
                  textColor='#333333'
                />
              </DatePickerField>
            )}
          </DateOffsetContainer>
        </>
      )}

      <SelectBox
        title='Athlete Calendar Visibility'
        options={calendarVisibilityOptions}
        value={values.calendarVisibility}
        onChangeFunction={(opt) => setFieldValue('calendarVisibility', opt)}
        styledProps={{ border: '1px solid #EEEEEE' }}
        titleStyledProps={{ fontWeight: 700 }}
        customStyles={selectBoxStyles}
        isClearable={false}
        isSearchable={false}
        height='auto'
        menuPortalTarget={document.body}
      />

      {!isNew && (
        <>
          <Text styledTextProps={{ fontWeight: '700' }}>Calendar History</Text>
          {data.length === 0 ? (
            <Text styledTextProps={{ color: '#9E9E9E' }}>No calendar history for this athlete.</Text>
          ) : (
            <TableWrapper>
              <table {...getTableProps()}>
                <thead>
                  {headerGroups.map((headerGroup) => (
                    <tr {...headerGroup.getHeaderGroupProps()}>
                      {headerGroup.headers.map((column) => (
                        <th {...column.getHeaderProps()}>{column.render('Header')}</th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody {...getTableBodyProps()}>
                  {rows.map((row) => {
                    prepareRow(row);
                    return (
                      <tr {...row.getRowProps()}>
                        {row.cells.map((cell) => (
                          <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </TableWrapper>
          )}
        </>
      )}
    </FormWrapper>
  );
};

AthleteCalendarForm.propTypes = {
  athleteCalendars: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    dateStart: PropTypes.string,
    dateEnd: PropTypes.string,
    dateOffset: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf([null])]),
    active: PropTypes.bool,
  })),
  calendars: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
  })).isRequired,
  calendarVisibilityOptions: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  })).isRequired,
  values: PropTypes.shape({
    calendarAssignment: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
    }),
    calendarVisibility: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
    }),
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    dateOffset: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf([null])]),
  }).isRequired,
  selectBoxStyles: PropTypes.instanceOf(Object).isRequired,
  setFieldValue: PropTypes.func.isRequired,
  onOpenNestedModal: PropTypes.func.isRequired,
  isNew: PropTypes.bool,
};

AthleteCalendarForm.defaultProps = {
  athleteCalendars: [],
  isNew: false,
};

export default AthleteCalendarForm;
